import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { Effect } from 'effect';

export interface ActiveStudioDocument {
  name: string;
  bytes: Uint8Array;
  revision: number;
  /**
   * The revision at which this document was opened. An edit keeps it, so it
   * names one open of one file however many times a tool changes it.
   */
  openedAt: number;
  /**
   * Okra Agent's key for the document (W.4.132): the first 32 hex characters of
   * the SHA-256 of the bytes as they were opened. The same file opened again
   * gets the same key, and an edit keeps it. Null until it is computed, a moment
   * after the open. It names the Agent's chats about the document and goes to
   * the Agent only, never to analytics.
   */
  documentKey: string | null;
  /**
   * The key could not be computed (the browser's SHA-256 failed). The Agent
   * panel then stops waiting for it and answers as with no PDF open. An edit
   * keeps it, as it keeps the key.
   */
  documentKeyFailed: boolean;
}

export interface StudioDocumentState {
  active: ActiveStudioDocument | null;
  revision: number;
}

const initialState: StudioDocumentState = {
  active: null,
  revision: 0,
};

const studioDocumentSlice = createSlice({
  name: 'studioDocument',
  initialState,
  reducers: {
    activeStudioDocumentChanged(
      state,
      action: PayloadAction<{ name: string; bytes: Uint8Array; edit?: boolean } | null>,
    ) {
      state.revision += 1;
      const previous = state.active;
      const edit = action.payload?.edit === true && previous !== null;
      state.active = action.payload
        ? {
            name: action.payload.name,
            bytes: action.payload.bytes,
            revision: state.revision,
            openedAt: edit && previous ? previous.openedAt : state.revision,
            documentKey: edit && previous ? previous.documentKey : null,
            documentKeyFailed: edit && previous ? previous.documentKeyFailed : false,
          }
        : null;
    },
    /** The key of the open made at `openedAt`; dropped when another document is open by now. */
    activeStudioDocumentKeyed(
      state,
      action: PayloadAction<{ openedAt: number; documentKey: string }>,
    ) {
      if (state.active?.openedAt === action.payload.openedAt) {
        state.active.documentKey = action.payload.documentKey;
      }
    },
    /** The key of the open made at `openedAt` could not be computed; dropped like a late key. */
    activeStudioDocumentKeyFailed(state, action: PayloadAction<{ openedAt: number }>) {
      if (state.active?.openedAt === action.payload.openedAt) {
        state.active.documentKeyFailed = true;
      }
    },
  },
});

export const {
  activeStudioDocumentChanged,
  activeStudioDocumentKeyed,
  activeStudioDocumentKeyFailed,
} = studioDocumentSlice.actions;
export const studioDocumentReducer = studioDocumentSlice.reducer;

/** Okra Agent's key for these bytes: the first 32 hex characters of their SHA-256. */
export const agentDocumentKeyEffect = Effect.fn('web.studioDocument.agentKey')(function* (
  bytes: Uint8Array,
) {
  const digest = yield* Effect.tryPromise(() =>
    crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>),
  );
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32);
});

export function agentDocumentKey(bytes: Uint8Array): Promise<string> {
  return Effect.runPromise(agentDocumentKeyEffect(bytes));
}

export const selectActiveStudioDocumentEffect = Effect.fn('web.studioDocument.selectActive')(
  function* (state: { studioDocument: StudioDocumentState }) {
    return state.studioDocument.active;
  },
);

export function selectActiveStudioDocument(state: {
  studioDocument: StudioDocumentState;
}): ActiveStudioDocument | null {
  return Effect.runSync(selectActiveStudioDocumentEffect(state));
}
